/**
 * Formula language (Notion formulas 2.0 style): literals, operators, `prop("Name")`, functions and the
 * `.method()` call syntax, lambdas through `current` / `index`, `let` / `lets`. Errors carry the source
 * position. Pure TypeScript with no DOM.
 */

export type FValue = number | string | boolean | Date | DateRange | PageRef | FValue[] | null;

export interface DateRange {
  kind: 'range';
  start: Date;
  end: Date;
}

export interface PageRef {
  kind: 'page';
  id: string;
  title: string;
}

export class FormulaError extends Error {
  constructor(
    message: string,
    readonly pos: number,
  ) {
    super(message);
  }
}

/** What the evaluator needs from the database: property lookup by name, for this row or a related page. */
export interface FormulaEnv {
  /** Value of a property on the current row (by property name). `undefined` = no such property. */
  prop(name: string): FValue | undefined;
  /** Value of a property on another page (relations: `current.prop("X")`). */
  pageProp?(pageId: string, name: string): FValue | undefined;
  now?: () => Date;
}

// ───────────── tokens ─────────────

type Tok =
  | { t: 'num'; v: number; pos: number }
  | { t: 'str'; v: string; pos: number }
  | { t: 'id'; v: string; pos: number }
  | { t: 'op'; v: string; pos: number }
  | { t: 'eof'; pos: number };

const OPS = ['==', '!=', '>=', '<=', '&&', '||', '+', '-', '*', '/', '%', '^', '>', '<', '!', '?', ':', '(', ')', '[', ']', ',', '.'];

function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (/\d/.test(c) || (c === '.' && /\d/.test(src[i + 1] ?? ''))) {
      const m = /^(\d+\.?\d*|\.\d+)(e[-+]?\d+)?/i.exec(src.slice(i))!;
      out.push({ t: 'num', v: Number(m[0]), pos: i });
      i += m[0].length;
      continue;
    }
    if (c === '"' || c === "'" || c === '“') {
      const close = c === '“' ? '”' : c;
      let j = i + 1;
      let s = '';
      while (j < src.length && src[j] !== close) {
        if (src[j] === '\\' && j + 1 < src.length) {
          const n = src[j + 1]!;
          s += n === 'n' ? '\n' : n === 't' ? '\t' : n;
          j += 2;
        } else s += src[j++];
      }
      if (j >= src.length) throw new FormulaError('Unterminated string', i);
      out.push({ t: 'str', v: s, pos: i });
      i = j + 1;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      const m = /^[A-Za-z_][\w]*/.exec(src.slice(i))!;
      out.push({ t: 'id', v: m[0], pos: i });
      i += m[0].length;
      continue;
    }
    const op = OPS.find((o) => src.startsWith(o, i));
    if (!op) throw new FormulaError(`Unexpected character "${c}"`, i);
    out.push({ t: 'op', v: op, pos: i });
    i += op.length;
  }
  out.push({ t: 'eof', pos: src.length });
  return out;
}

// ───────────── AST ─────────────

export type Node =
  | { k: 'lit'; v: FValue; pos: number }
  | { k: 'list'; items: Node[]; pos: number }
  | { k: 'var'; name: string; pos: number }
  | { k: 'un'; op: string; arg: Node; pos: number }
  | { k: 'bin'; op: string; a: Node; b: Node; pos: number }
  | { k: 'tern'; c: Node; a: Node; b: Node; pos: number }
  | { k: 'call'; name: string; args: Node[]; pos: number };

const BIN_PREC: Record<string, number> = { '||': 1, or: 1, '&&': 2, and: 2, '==': 3, '!=': 3, '>': 4, '<': 4, '>=': 4, '<=': 4, '+': 5, '-': 5, '*': 6, '/': 6, '%': 6, '^': 7 };

export function parse(src: string): Node {
  const toks = tokenize(src);
  let p = 0;
  const peek = () => toks[p]!;
  const next = () => toks[p++]!;
  const isOp = (v: string) => peek().t === 'op' && (peek() as { v: string }).v === v;
  const expect = (v: string) => {
    const t = peek();
    if (t.t !== 'op' || t.v !== v) throw new FormulaError(t.t === 'eof' ? `Expected "${v}" but the formula ended` : `Expected "${v}"`, t.pos);
    p++;
  };
  const binOp = (): string | null => {
    const t = peek();
    if (t.t === 'op' && BIN_PREC[t.v] !== undefined) return t.v;
    if (t.t === 'id' && (t.v === 'and' || t.v === 'or')) return t.v;
    return null;
  };

  const ternary = (): Node => {
    const c = binary(0);
    if (isOp('?')) {
      const pos = next().pos;
      const a = ternary();
      expect(':');
      const b = ternary();
      return { k: 'tern', c, a, b, pos };
    }
    return c;
  };

  const binary = (minPrec: number): Node => {
    let left = unary();
    for (;;) {
      const op = binOp();
      if (!op) return left;
      const prec = BIN_PREC[op]!;
      if (prec < minPrec) return left;
      const pos = next().pos;
      const right = binary(op === '^' ? prec : prec + 1);
      left = { k: 'bin', op: op === 'and' ? '&&' : op === 'or' ? '||' : op, a: left, b: right, pos };
    }
  };

  const unary = (): Node => {
    const t = peek();
    if ((t.t === 'op' && (t.v === '-' || t.v === '!' || t.v === '+')) || (t.t === 'id' && t.v === 'not' && toks[p + 1]?.t !== 'op')) {
      next();
      const arg = unary();
      const op = t.t === 'id' ? '!' : (t as { v: string }).v;
      return op === '+' ? arg : { k: 'un', op, arg, pos: t.pos };
    }
    return postfix(primary());
  };

  const args = (): Node[] => {
    expect('(');
    const out: Node[] = [];
    if (!isOp(')')) {
      out.push(ternary());
      while (isOp(',')) {
        next();
        out.push(ternary());
      }
    }
    expect(')');
    return out;
  };

  const postfix = (node: Node): Node => {
    while (isOp('.')) {
      next();
      const name = next();
      if (name.t !== 'id') throw new FormulaError('Expected a function name after "."', name.pos);
      const rest = isOp('(') ? args() : [];
      node = { k: 'call', name: name.v, args: [node, ...rest], pos: name.pos };
    }
    return node;
  };

  const primary = (): Node => {
    const t = next();
    if (t.t === 'num') return { k: 'lit', v: t.v, pos: t.pos };
    if (t.t === 'str') return { k: 'lit', v: t.v, pos: t.pos };
    if (t.t === 'op' && t.v === '(') {
      const e = ternary();
      expect(')');
      return e;
    }
    if (t.t === 'op' && t.v === '[') {
      const items: Node[] = [];
      if (!isOp(']')) {
        items.push(ternary());
        while (isOp(',')) {
          next();
          items.push(ternary());
        }
      }
      expect(']');
      return { k: 'list', items, pos: t.pos };
    }
    if (t.t === 'id') {
      if (t.v === 'true' || t.v === 'false') return { k: 'lit', v: t.v === 'true', pos: t.pos };
      if (isOp('(')) return { k: 'call', name: t.v, args: args(), pos: t.pos };
      return { k: 'var', name: t.v, pos: t.pos };
    }
    if (t.t === 'eof') throw new FormulaError('The formula ended unexpectedly', t.pos);
    throw new FormulaError(`Unexpected "${(t as { v: string }).v}"`, t.pos);
  };

  const root = ternary();
  if (peek().t !== 'eof') throw new FormulaError(`Unexpected "${(peek() as { v?: string }).v ?? ''}"`, peek().pos);
  return root;
}

// ───────────── values ─────────────

const isDate = (v: FValue): v is Date => v instanceof Date;
const isRange = (v: FValue): v is DateRange => !!v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date) && (v as DateRange).kind === 'range';
const isPage = (v: FValue): v is PageRef => !!v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date) && (v as PageRef).kind === 'page';

export function typeName(v: FValue): string {
  if (v === null) return 'empty';
  if (Array.isArray(v)) return 'list';
  if (isDate(v) || isRange(v)) return 'date';
  if (isPage(v)) return 'page';
  if (typeof v === 'string') return 'text';
  if (typeof v === 'boolean') return 'checkbox';
  return 'number';
}

function startOf(v: FValue): Date | null {
  if (isDate(v)) return v;
  if (isRange(v)) return v.start;
  return null;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Formats a date with tokens: YYYY YY MMMM MMM MM M DD D dddd ddd HH H hh h mm A a. */
export function formatDateWith(d: Date, fmt: string): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const h12 = d.getHours() % 12 || 12;
  const map: Record<string, string> = {
    YYYY: String(d.getFullYear()),
    YY: String(d.getFullYear()).slice(-2),
    MMMM: MONTHS[d.getMonth()]!,
    MMM: MONTHS[d.getMonth()]!.slice(0, 3),
    MM: pad(d.getMonth() + 1),
    M: String(d.getMonth() + 1),
    DD: pad(d.getDate()),
    D: String(d.getDate()),
    dddd: DAYS[d.getDay()]!,
    ddd: DAYS[d.getDay()]!.slice(0, 3),
    HH: pad(d.getHours()),
    H: String(d.getHours()),
    hh: pad(h12),
    h: String(h12),
    mm: pad(d.getMinutes()),
    A: d.getHours() < 12 ? 'AM' : 'PM',
    a: d.getHours() < 12 ? 'am' : 'pm',
  };
  return fmt.replace(/YYYY|YY|MMMM|MMM|MM|M|DD|D|dddd|ddd|HH|H|hh|h|mm|A|a/g, (t) => map[t]!);
}

/** Display text for a formula value. */
export function formatFValue(v: FValue): string {
  if (v === null) return '';
  if (Array.isArray(v)) return v.map(formatFValue).join(', ');
  if (isDate(v)) return v.getHours() || v.getMinutes() ? formatDateWith(v, 'MMMM D, YYYY h:mm A') : formatDateWith(v, 'MMMM D, YYYY');
  if (isRange(v)) return `${formatFValue(v.start)} → ${formatFValue(v.end)}`;
  if (isPage(v)) return v.title;
  if (typeof v === 'number') return String(Math.round(v * 1e10) / 1e10);
  return String(v);
}

function isoWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

const UNITS: Record<string, string> = {
  year: 'years', years: 'years', quarter: 'quarters', quarters: 'quarters', month: 'months', months: 'months', week: 'weeks', weeks: 'weeks', day: 'days', days: 'days', hour: 'hours', hours: 'hours', minute: 'minutes', minutes: 'minutes', second: 'seconds', seconds: 'seconds', millisecond: 'milliseconds', milliseconds: 'milliseconds',
};

function addUnits(d: Date, n: number, unit: string, pos: number): Date {
  const u = UNITS[unit];
  if (!u) throw new FormulaError(`Unknown date unit "${unit}"`, pos);
  const r = new Date(d.getTime());
  if (u === 'years') r.setFullYear(r.getFullYear() + n);
  else if (u === 'quarters') r.setMonth(r.getMonth() + 3 * n);
  else if (u === 'months') r.setMonth(r.getMonth() + n);
  else if (u === 'weeks') r.setDate(r.getDate() + 7 * n);
  else if (u === 'days') r.setDate(r.getDate() + n);
  else if (u === 'hours') r.setTime(r.getTime() + n * 3_600_000);
  else if (u === 'minutes') r.setTime(r.getTime() + n * 60_000);
  else if (u === 'seconds') r.setTime(r.getTime() + n * 1000);
  else r.setTime(r.getTime() + n);
  return r;
}

function between(a: Date, b: Date, unit: string, pos: number): number {
  const u = UNITS[unit];
  if (!u) throw new FormulaError(`Unknown date unit "${unit}"`, pos);
  const ms = a.getTime() - b.getTime();
  const trunc = (x: number) => (x < 0 ? Math.ceil(x) : Math.floor(x));
  if (u === 'years' || u === 'quarters' || u === 'months') {
    let months = (a.getFullYear() - b.getFullYear()) * 12 + (a.getMonth() - b.getMonth());
    if (months > 0 && a.getDate() < b.getDate()) months--;
    if (months < 0 && a.getDate() > b.getDate()) months++;
    return u === 'years' ? trunc(months / 12) : u === 'quarters' ? trunc(months / 3) : months;
  }
  const div = { weeks: 604_800_000, days: 86_400_000, hours: 3_600_000, minutes: 60_000, seconds: 1000, milliseconds: 1 }[u]!;
  return trunc(ms / div);
}

function toRegex(pattern: FValue, flags: string, pos: number): RegExp {
  try {
    return new RegExp(String(pattern), flags);
  } catch {
    throw new FormulaError(`Invalid regular expression "${String(pattern)}"`, pos);
  }
}

function equal(a: FValue, b: FValue): boolean {
  if (isDate(a) && isDate(b)) return a.getTime() === b.getTime();
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => equal(x, b[i]!));
  if (isPage(a) && isPage(b)) return a.id === b.id;
  return a === b;
}

function compare(a: FValue, b: FValue): number {
  const da = startOf(a);
  const db = startOf(b);
  if (da && db) return da.getTime() - db.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return formatFValue(a).localeCompare(formatFValue(b));
}

function isEmptyF(v: FValue): boolean {
  return v === null || v === '' || v === 0 || v === false || (Array.isArray(v) && v.length === 0);
}

// ───────────── evaluation ─────────────

type Scope = Map<string, FValue>;

class Evaluator {
  private depth = 0;
  constructor(private readonly env: FormulaEnv) {}

  eval(n: Node, scope: Scope): FValue {
    switch (n.k) {
      case 'lit':
        return n.v;
      case 'list':
        return n.items.map((i) => this.eval(i, scope));
      case 'var': {
        if (scope.has(n.name)) return scope.get(n.name)!;
        if (n.name === 'e') return Math.E;
        if (n.name === 'pi') return Math.PI;
        throw new FormulaError(`Unknown variable "${n.name}"`, n.pos);
      }
      case 'un': {
        const v = this.eval(n.arg, scope);
        if (n.op === '!') return !this.bool(v, n.pos);
        return -this.num(v, n.pos, 'negate');
      }
      case 'tern':
        return this.bool(this.eval(n.c, scope), n.pos) ? this.eval(n.a, scope) : this.eval(n.b, scope);
      case 'bin':
        return this.binary(n, scope);
      case 'call':
        return this.call(n, scope);
    }
  }

  private num(v: FValue, pos: number, what: string): number {
    if (typeof v === 'number') return v;
    if (v === null) return 0;
    throw new FormulaError(`Cannot ${what} ${typeName(v)}; expected a number`, pos);
  }

  private str(v: FValue, pos: number): string {
    if (typeof v === 'string') return v;
    if (v === null) return '';
    throw new FormulaError(`Expected text but got ${typeName(v)}`, pos);
  }

  private bool(v: FValue, pos: number): boolean {
    if (typeof v === 'boolean') return v;
    if (v === null) return false;
    throw new FormulaError(`Expected a checkbox (true/false) but got ${typeName(v)}`, pos);
  }

  private list(v: FValue, pos: number): FValue[] {
    if (Array.isArray(v)) return v;
    if (v === null) return [];
    throw new FormulaError(`Expected a list but got ${typeName(v)}`, pos);
  }

  private date(v: FValue, pos: number): Date {
    const d = startOf(v);
    if (d) return d;
    throw new FormulaError(`Expected a date but got ${typeName(v)}`, pos);
  }

  private binary(n: Extract<Node, { k: 'bin' }>, scope: Scope): FValue {
    if (n.op === '&&') return this.bool(this.eval(n.a, scope), n.a.pos) && this.bool(this.eval(n.b, scope), n.b.pos);
    if (n.op === '||') return this.bool(this.eval(n.a, scope), n.a.pos) || this.bool(this.eval(n.b, scope), n.b.pos);
    const a = this.eval(n.a, scope);
    const b = this.eval(n.b, scope);
    switch (n.op) {
      case '+':
        if (typeof a === 'string' || typeof b === 'string') {
          if ((typeof a === 'string' || a === null) && (typeof b === 'string' || b === null)) return (a ?? '') + (b ?? '');
          throw new FormulaError(`Cannot add ${typeName(a)} and ${typeName(b)}`, n.pos);
        }
        return this.num(a, n.pos, 'add') + this.num(b, n.pos, 'add');
      case '-':
        return this.num(a, n.pos, 'subtract') - this.num(b, n.pos, 'subtract');
      case '*':
        return this.num(a, n.pos, 'multiply') * this.num(b, n.pos, 'multiply');
      case '/': {
        const d = this.num(b, n.pos, 'divide');
        return d === 0 ? null : this.num(a, n.pos, 'divide') / d;
      }
      case '%':
        return this.num(a, n.pos, 'take the remainder of') % this.num(b, n.pos, 'take the remainder of');
      case '^':
        return Math.pow(this.num(a, n.pos, 'raise'), this.num(b, n.pos, 'raise'));
      case '==':
        return equal(a, b);
      case '!=':
        return !equal(a, b);
      case '>':
        return compare(a, b) > 0;
      case '<':
        return compare(a, b) < 0;
      case '>=':
        return compare(a, b) >= 0;
      case '<=':
        return compare(a, b) <= 0;
    }
    throw new FormulaError(`Unknown operator "${n.op}"`, n.pos);
  }

  private lambda(list: FValue[], body: Node, scope: Scope, fn: (v: FValue, i: number, r: FValue) => boolean | void) {
    for (let i = 0; i < list.length; i++) {
      const s = new Map(scope);
      s.set('current', list[i]!);
      s.set('index', i);
      if (fn(list[i]!, i, this.eval(body, s)) === true) return;
    }
  }

  private arity(n: Extract<Node, { k: 'call' }>, min: number, max = min) {
    if (n.args.length < min || n.args.length > max) {
      const want = min === max ? `${min}` : max === Infinity ? `at least ${min}` : `${min} to ${max}`;
      throw new FormulaError(`${n.name}() takes ${want} argument${min === 1 && max === 1 ? '' : 's'}, got ${n.args.length}`, n.pos);
    }
  }

  private call(n: Extract<Node, { k: 'call' }>, scope: Scope): FValue {
    const name = n.name;
    const ev = (i: number) => this.eval(n.args[i]!, scope);
    const all = () => n.args.map((a) => this.eval(a, scope));
    const nums = () => all().flatMap((v) => (Array.isArray(v) ? v : [v])).filter((v) => v !== null).map((v) => this.num(v, n.pos, name));
    const pos = n.pos;
    switch (name) {
      case 'prop': {
        if (n.args.length === 2) {
          const page = ev(0);
          const pname = this.str(ev(1), n.args[1]!.pos);
          if (!isPage(page)) throw new FormulaError(`prop() on ${typeName(page)}: expected a page`, pos);
          const v = this.env.pageProp?.(page.id, pname);
          if (v === undefined) throw new FormulaError(`Unknown property "${pname}"`, n.args[1]!.pos);
          return v;
        }
        this.arity(n, 1);
        const pname = this.str(ev(0), n.args[0]!.pos);
        if (++this.depth > 32) throw new FormulaError('Formula references itself', pos);
        try {
          const v = this.env.prop(pname);
          if (v === undefined) throw new FormulaError(`Unknown property "${pname}"`, n.args[0]!.pos);
          return v;
        } finally {
          this.depth--;
        }
      }
      // logic
      case 'if':
        this.arity(n, 3);
        return this.bool(ev(0), n.args[0]!.pos) ? ev(1) : ev(2);
      case 'ifs': {
        if (n.args.length < 3 || n.args.length % 2 === 0) throw new FormulaError('ifs() takes condition/value pairs and a final else value', pos);
        for (let i = 0; i + 1 < n.args.length; i += 2) if (this.bool(ev(i), n.args[i]!.pos)) return ev(i + 1);
        return ev(n.args.length - 1);
      }
      case 'and':
        this.arity(n, 1, Infinity);
        return n.args.every((a) => this.bool(this.eval(a, scope), a.pos));
      case 'or':
        this.arity(n, 1, Infinity);
        return n.args.some((a) => this.bool(this.eval(a, scope), a.pos));
      case 'not':
        this.arity(n, 1);
        return !this.bool(ev(0), pos);
      case 'empty':
        this.arity(n, 1);
        return isEmptyF(ev(0));
      // text
      case 'length': {
        this.arity(n, 1);
        const v = ev(0);
        return Array.isArray(v) ? v.length : this.str(v === null ? '' : typeof v === 'string' ? v : formatFValue(v), pos).length;
      }
      case 'concat': {
        this.arity(n, 1, Infinity);
        const vs = all();
        if (vs.every((v) => Array.isArray(v) || v === null)) return vs.flatMap((v) => (v as FValue[]) ?? []);
        return vs.map((v) => this.str(v, pos)).join('');
      }
      case 'join': {
        this.arity(n, 2);
        return this.list(ev(0), pos).map(formatFValue).join(this.str(ev(1), pos));
      }
      case 'slice': {
        this.arity(n, 2, 3);
        const v = ev(0);
        const s = this.num(ev(1), pos, 'slice with');
        const e = n.args.length > 2 ? this.num(ev(2), pos, 'slice with') : undefined;
        return Array.isArray(v) ? v.slice(s, e) : this.str(v, pos).slice(s, e);
      }
      case 'contains': {
        this.arity(n, 2);
        const v = ev(0);
        const x = ev(1);
        if (Array.isArray(v)) return v.some((i) => equal(i, x));
        return this.str(typeof v === 'string' ? v : formatFValue(v), pos).includes(this.str(x, pos));
      }
      case 'includes': {
        this.arity(n, 2);
        const x = ev(1);
        return this.list(ev(0), pos).some((i) => equal(i, x));
      }
      case 'replace':
      case 'replaceAll': {
        this.arity(n, 3);
        const re = toRegex(ev(1), name === 'replaceAll' ? 'g' : '', n.args[1]!.pos);
        return this.str(ev(0), pos).replace(re, this.str(ev(2), pos));
      }
      case 'test': {
        this.arity(n, 2);
        return toRegex(ev(1), '', n.args[1]!.pos).test(formatFValue(ev(0)));
      }
      case 'match': {
        this.arity(n, 2);
        return [...this.str(ev(0), pos).matchAll(toRegex(ev(1), 'g', n.args[1]!.pos))].map((m) => m[0]);
      }
      case 'lower':
        this.arity(n, 1);
        return this.str(ev(0), pos).toLowerCase();
      case 'upper':
        this.arity(n, 1);
        return this.str(ev(0), pos).toUpperCase();
      case 'trim':
        this.arity(n, 1);
        return this.str(ev(0), pos).trim();
      case 'repeat': {
        this.arity(n, 2);
        return this.str(ev(0), pos).repeat(Math.max(0, this.num(ev(1), pos, 'repeat')));
      }
      case 'split': {
        this.arity(n, 2);
        return this.str(ev(0), pos).split(this.str(ev(1), pos));
      }
      case 'format':
        this.arity(n, 1);
        return formatFValue(ev(0));
      case 'toNumber': {
        this.arity(n, 1);
        const v = ev(0);
        if (typeof v === 'number') return v;
        if (typeof v === 'boolean') return v ? 1 : 0;
        if (isDate(v)) return v.getTime();
        const x = parseFloat(String(v ?? ''));
        return Number.isNaN(x) ? null : x;
      }
      // math
      case 'round': {
        this.arity(n, 1, 2);
        const places = n.args.length > 1 ? this.num(ev(1), pos, 'round to') : 0;
        const f = Math.pow(10, places);
        return Math.round(this.num(ev(0), pos, 'round') * f) / f;
      }
      case 'ceil':
        this.arity(n, 1);
        return Math.ceil(this.num(ev(0), pos, 'ceil'));
      case 'floor':
        this.arity(n, 1);
        return Math.floor(this.num(ev(0), pos, 'floor'));
      case 'abs':
        this.arity(n, 1);
        return Math.abs(this.num(ev(0), pos, 'take the absolute value of'));
      case 'sqrt':
        this.arity(n, 1);
        return Math.sqrt(this.num(ev(0), pos, 'take the square root of'));
      case 'cbrt':
        this.arity(n, 1);
        return Math.cbrt(this.num(ev(0), pos, 'take the cube root of'));
      case 'pow':
        this.arity(n, 2);
        return Math.pow(this.num(ev(0), pos, 'raise'), this.num(ev(1), pos, 'raise'));
      case 'exp':
        this.arity(n, 1);
        return Math.exp(this.num(ev(0), pos, 'exp'));
      case 'ln':
        this.arity(n, 1);
        return Math.log(this.num(ev(0), pos, 'ln'));
      case 'log10':
        this.arity(n, 1);
        return Math.log10(this.num(ev(0), pos, 'log10'));
      case 'sign':
        this.arity(n, 1);
        return Math.sign(this.num(ev(0), pos, 'sign'));
      case 'mod':
        this.arity(n, 2);
        return this.num(ev(0), pos, 'mod') % this.num(ev(1), pos, 'mod');
      case 'min':
      case 'max': {
        this.arity(n, 1, Infinity);
        const v = nums();
        return v.length ? (name === 'min' ? Math.min(...v) : Math.max(...v)) : null;
      }
      case 'sum':
        this.arity(n, 1, Infinity);
        return nums().reduce((a, b) => a + b, 0);
      case 'mean': {
        this.arity(n, 1, Infinity);
        const v = nums();
        return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
      }
      case 'median': {
        this.arity(n, 1, Infinity);
        const v = nums().sort((a, b) => a - b);
        if (!v.length) return null;
        const m = Math.floor(v.length / 2);
        return v.length % 2 ? v[m]! : (v[m - 1]! + v[m]!) / 2;
      }
      // dates
      case 'now':
        this.arity(n, 0);
        return this.env.now?.() ?? new Date();
      case 'today': {
        this.arity(n, 0);
        const d = this.env.now?.() ?? new Date();
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
      }
      case 'dateAdd':
      case 'dateSubtract': {
        this.arity(n, 3);
        const amount = this.num(ev(1), pos, name);
        return addUnits(this.date(ev(0), n.args[0]!.pos), name === 'dateAdd' ? amount : -amount, this.str(ev(2), n.args[2]!.pos), n.args[2]!.pos);
      }
      case 'dateBetween':
        this.arity(n, 3);
        return between(this.date(ev(0), n.args[0]!.pos), this.date(ev(1), n.args[1]!.pos), this.str(ev(2), n.args[2]!.pos), n.args[2]!.pos);
      case 'dateRange':
        this.arity(n, 2);
        return { kind: 'range', start: this.date(ev(0), pos), end: this.date(ev(1), pos) };
      case 'dateStart': {
        this.arity(n, 1);
        return this.date(ev(0), pos);
      }
      case 'dateEnd': {
        this.arity(n, 1);
        const v = ev(0);
        return isRange(v) ? v.end : this.date(v, pos);
      }
      case 'formatDate':
        this.arity(n, 2);
        return formatDateWith(this.date(ev(0), pos), this.str(ev(1), pos));
      case 'parseDate': {
        this.arity(n, 1);
        const s = this.str(ev(0), pos);
        const t = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s);
        if (Number.isNaN(t)) throw new FormulaError(`Cannot parse "${s}" as a date`, pos);
        return new Date(t);
      }
      case 'fromTimestamp':
        this.arity(n, 1);
        return new Date(this.num(ev(0), pos, 'convert'));
      case 'timestamp':
        this.arity(n, 1);
        return this.date(ev(0), pos).getTime();
      case 'year':
        this.arity(n, 1);
        return this.date(ev(0), pos).getFullYear();
      case 'month':
        this.arity(n, 1);
        return this.date(ev(0), pos).getMonth() + 1;
      case 'week':
        this.arity(n, 1);
        return isoWeek(this.date(ev(0), pos));
      case 'date':
        this.arity(n, 1);
        return this.date(ev(0), pos).getDate();
      case 'day':
        this.arity(n, 1);
        return this.date(ev(0), pos).getDay() || 7;
      case 'hour':
        this.arity(n, 1);
        return this.date(ev(0), pos).getHours();
      case 'minute':
        this.arity(n, 1);
        return this.date(ev(0), pos).getMinutes();
      // variables
      case 'let': {
        this.arity(n, 3);
        const v = n.args[0]!;
        if (v.k !== 'var') throw new FormulaError('let() needs a variable name first', v.pos);
        const s = new Map(scope);
        s.set(v.name, ev(1));
        return this.eval(n.args[2]!, s);
      }
      case 'lets': {
        if (n.args.length < 3 || n.args.length % 2 === 0) throw new FormulaError('lets() takes name/value pairs and a final expression', pos);
        const s = new Map(scope);
        for (let i = 0; i + 1 < n.args.length; i += 2) {
          const v = n.args[i]!;
          if (v.k !== 'var') throw new FormulaError('lets() needs variable names', v.pos);
          s.set(v.name, this.eval(n.args[i + 1]!, s));
        }
        return this.eval(n.args[n.args.length - 1]!, s);
      }
      // lists
      case 'map': {
        this.arity(n, 2);
        const out: FValue[] = [];
        this.lambda(this.list(ev(0), pos), n.args[1]!, scope, (_v, _i, r) => void out.push(r));
        return out;
      }
      case 'filter': {
        this.arity(n, 2);
        const out: FValue[] = [];
        this.lambda(this.list(ev(0), pos), n.args[1]!, scope, (v, _i, r) => void (this.bool(r, n.args[1]!.pos) && out.push(v)));
        return out;
      }
      case 'find': {
        this.arity(n, 2);
        let found: FValue = null;
        this.lambda(this.list(ev(0), pos), n.args[1]!, scope, (v, _i, r) => {
          if (this.bool(r, n.args[1]!.pos)) {
            found = v;
            return true;
          }
        });
        return found;
      }
      case 'findIndex': {
        this.arity(n, 2);
        let found = -1;
        this.lambda(this.list(ev(0), pos), n.args[1]!, scope, (_v, i, r) => {
          if (this.bool(r, n.args[1]!.pos)) {
            found = i;
            return true;
          }
        });
        return found;
      }
      case 'some':
      case 'every': {
        this.arity(n, 2);
        let res = name === 'every';
        this.lambda(this.list(ev(0), pos), n.args[1]!, scope, (_v, _i, r) => {
          const b = this.bool(r, n.args[1]!.pos);
          if (name === 'some' && b) {
            res = true;
            return true;
          }
          if (name === 'every' && !b) {
            res = false;
            return true;
          }
        });
        return res;
      }
      case 'first':
        this.arity(n, 1);
        return this.list(ev(0), pos)[0] ?? null;
      case 'last':
        this.arity(n, 1);
        return this.list(ev(0), pos).at(-1) ?? null;
      case 'at': {
        this.arity(n, 2);
        return this.list(ev(0), pos).at(this.num(ev(1), pos, 'index with')) ?? null;
      }
      case 'sort': {
        this.arity(n, 1, 2);
        const l = this.list(ev(0), pos);
        if (n.args.length === 1) return [...l].sort(compare);
        const keyed: { v: FValue; k: FValue }[] = [];
        this.lambda(l, n.args[1]!, scope, (v, _i, r) => void keyed.push({ v, k: r }));
        return keyed.sort((a, b) => compare(a.k, b.k)).map((x) => x.v);
      }
      case 'reverse':
        this.arity(n, 1);
        return [...this.list(ev(0), pos)].reverse();
      case 'unique': {
        this.arity(n, 1);
        const out: FValue[] = [];
        for (const v of this.list(ev(0), pos)) if (!out.some((o) => equal(o, v))) out.push(v);
        return out;
      }
      case 'flat':
        this.arity(n, 1);
        return this.list(ev(0), pos).flatMap((v) => (Array.isArray(v) ? v : [v]));
      case 'count':
        this.arity(n, 1);
        return this.list(ev(0), pos).length;
      case 'id':
        this.arity(n, 1);
        return isPage(ev(0)) ? (ev(0) as PageRef).id : null;
    }
    throw new FormulaError(`Unknown function "${name}"`, pos);
  }
}

export interface FormulaResult {
  value: FValue;
  error: FormulaError | null;
}

/** Parses and evaluates a formula. Errors (syntax, unknown property, type mismatch) come back with a position. */
export function evaluateFormula(expression: string, env: FormulaEnv): FormulaResult {
  if (!expression.trim()) return { value: null, error: null };
  try {
    const ast = parse(expression);
    return { value: new Evaluator(env).eval(ast, new Map()), error: null };
  } catch (e) {
    if (e instanceof FormulaError) return { value: null, error: e };
    throw e;
  }
}

/** Every function the language offers, with a one-line description, for the formula editor's function list. */
export const FORMULA_FUNCTIONS: { name: string; group: string; doc: string; example: string }[] = [
  ['if', 'Logic', 'Returns the second argument if the condition is true, else the third.', 'if(prop("Done"), "✓", "…")'],
  ['ifs', 'Logic', 'Checks conditions in order and returns the value of the first true one.', 'ifs(x > 10, "big", x > 5, "medium", "small")'],
  ['and', 'Logic', 'True when all arguments are true.', 'and(true, false)'],
  ['or', 'Logic', 'True when any argument is true.', 'or(true, false)'],
  ['not', 'Logic', 'Inverts a checkbox value.', 'not(true)'],
  ['empty', 'Logic', 'True for empty values: "", 0, false, [] and blank properties.', 'empty(prop("Notes"))'],
  ['length', 'Text', 'Number of characters in text, or items in a list.', 'length("Lumen")'],
  ['concat', 'Lists', 'Joins lists (or text) together.', 'concat([1, 2], [3])'],
  ['join', 'Lists', 'Joins list items into text with a separator.', 'join(["a", "b"], ", ")'],
  ['slice', 'Text', 'Part of text or a list from start to end.', 'slice("Hello", 1, 3)'],
  ['contains', 'Text', 'True when the text contains the search text.', 'contains("Lumen", "um")'],
  ['replace', 'Text', 'Replaces the first match of a pattern.', 'replace("aaa", "a", "b")'],
  ['replaceAll', 'Text', 'Replaces every match of a pattern.', 'replaceAll("aaa", "a", "b")'],
  ['test', 'Text', 'True when the text matches a regular expression.', 'test("abc", "b")'],
  ['match', 'Text', 'All matches of a regular expression, as a list.', 'match("a1b2", "\\\\d")'],
  ['lower', 'Text', 'Lowercase text.', 'lower("ABC")'],
  ['upper', 'Text', 'Uppercase text.', 'upper("abc")'],
  ['trim', 'Text', 'Removes leading and trailing spaces.', 'trim("  a  ")'],
  ['format', 'Text', 'Any value as text.', 'format(42)'],
  ['toNumber', 'Number', 'Parses text as a number.', 'toNumber("42")'],
  ['round', 'Number', 'Rounds to the nearest integer or number of places.', 'round(3.14159, 2)'],
  ['ceil', 'Number', 'Rounds up.', 'ceil(1.2)'],
  ['floor', 'Number', 'Rounds down.', 'floor(1.8)'],
  ['abs', 'Number', 'Absolute value.', 'abs(-3)'],
  ['sqrt', 'Number', 'Square root.', 'sqrt(16)'],
  ['pow', 'Number', 'Raises a number to a power.', 'pow(2, 10)'],
  ['min', 'Number', 'Smallest number.', 'min(3, 1, 2)'],
  ['max', 'Number', 'Largest number.', 'max(3, 1, 2)'],
  ['sum', 'Number', 'Adds numbers (or a list of numbers).', 'sum([1, 2, 3])'],
  ['mean', 'Number', 'Average.', 'mean(1, 2, 3)'],
  ['median', 'Number', 'Middle value.', 'median(1, 2, 9)'],
  ['now', 'Date', 'The current date and time.', 'now()'],
  ['today', 'Date', "Today's date without time.", 'today()'],
  ['dateAdd', 'Date', 'Adds an amount of a unit to a date.', 'dateAdd(now(), 3, "days")'],
  ['dateSubtract', 'Date', 'Subtracts an amount of a unit from a date.', 'dateSubtract(now(), 1, "months")'],
  ['dateBetween', 'Date', 'Difference between two dates in a unit.', 'dateBetween(prop("Due"), now(), "days")'],
  ['dateStart', 'Date', 'Start of a date range.', 'dateStart(prop("Dates"))'],
  ['dateEnd', 'Date', 'End of a date range.', 'dateEnd(prop("Dates"))'],
  ['formatDate', 'Date', 'Formats a date (YYYY, MM, DD, HH, mm…).', 'formatDate(now(), "YYYY-MM-DD")'],
  ['parseDate', 'Date', 'Parses an ISO date.', 'parseDate("2026-01-31")'],
  ['timestamp', 'Date', 'Milliseconds since 1970.', 'timestamp(now())'],
  ['year', 'Date', 'Year of a date.', 'year(now())'],
  ['month', 'Date', 'Month of a date (1–12).', 'month(now())'],
  ['week', 'Date', 'ISO week of the year.', 'week(now())'],
  ['day', 'Date', 'Day of the week (1 Monday – 7 Sunday).', 'day(now())'],
  ['hour', 'Date', 'Hour (0–23).', 'hour(now())'],
  ['minute', 'Date', 'Minute (0–59).', 'minute(now())'],
  ['let', 'Variables', 'Names a value for use in an expression.', 'let(x, 2, x * x)'],
  ['lets', 'Variables', 'Names several values.', 'lets(a, 1, b, 2, a + b)'],
  ['map', 'Lists', 'Transforms each item (use current and index).', 'map([1, 2], current * 2)'],
  ['filter', 'Lists', 'Keeps items matching a condition.', 'filter([1, 2, 3], current > 1)'],
  ['find', 'Lists', 'First item matching a condition.', 'find([1, 2, 3], current > 1)'],
  ['some', 'Lists', 'True if any item matches.', 'some([1, 2], current > 1)'],
  ['every', 'Lists', 'True if all items match.', 'every([1, 2], current > 0)'],
  ['first', 'Lists', 'First item.', 'first([1, 2])'],
  ['last', 'Lists', 'Last item.', 'last([1, 2])'],
  ['at', 'Lists', 'Item at an index (negative counts from the end).', 'at([1, 2, 3], -1)'],
  ['sort', 'Lists', 'Sorts items, optionally by an expression.', 'sort([3, 1, 2])'],
  ['reverse', 'Lists', 'Reverses a list.', 'reverse([1, 2])'],
  ['unique', 'Lists', 'Removes duplicate items.', 'unique([1, 1, 2])'],
  ['flat', 'Lists', 'Flattens nested lists.', 'flat([[1], [2, 3]])'],
  ['prop', 'Properties', 'The value of a property of this row.', 'prop("Name")'],
].map(([name, group, doc, example]) => ({ name: name!, group: group!, doc: doc!, example: example! }));
